import type { FabricObject } from 'fabric';
import type { SmartObjectData, ToolCategory, ToolRecipe } from './types';
import { smartObjectData } from './types';

/**
 * Recipe registry. Recipes self-register at module load (each recipe module
 * calls registerRecipe); import 'src/tools' to get the full catalog.
 */

const recipes = new Map<string, ToolRecipe>();

export function registerRecipe(recipe: ToolRecipe): void {
  if (recipes.has(recipe.id)) {
    throw new Error(`Duplicate tool recipe id "${recipe.id}"`);
  }
  recipes.set(recipe.id, recipe);
}

export function getRecipe(id: string): ToolRecipe | undefined {
  return recipes.get(id);
}

export function listRecipes(category?: ToolCategory): ToolRecipe[] {
  const all = [...recipes.values()];
  return category ? all.filter((r) => r.category === category) : all;
}

/** Generate a smart object from a recipe id + params merged over defaults. */
export async function generateSmartObject(
  id: string,
  params: Record<string, unknown> = {},
): Promise<FabricObject> {
  const recipe = recipes.get(id);
  if (!recipe) {
    throw new Error(`Unknown tool recipe "${id}". Registered: ${[...recipes.keys()].join(', ')}`);
  }
  return recipe.generate({ ...recipe.defaults, ...params });
}

/**
 * Regenerate an existing smart object (same recipe, new params), preserving
 * placement/scale/rotation. Returns the replacement; the caller swaps it in.
 */
export async function regenerateSmartObject(
  obj: FabricObject,
  newParams: Record<string, unknown>,
): Promise<FabricObject> {
  const data: SmartObjectData | undefined = smartObjectData(obj);
  if (!data) throw new Error('Object has no smart-object metadata — cannot regenerate.');
  const fresh = await generateSmartObject(data.tool, { ...data.params, ...newParams });
  fresh.set({
    left: obj.left,
    top: obj.top,
    scaleX: obj.scaleX,
    scaleY: obj.scaleY,
    angle: obj.angle,
  });
  return fresh;
}
